import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface StripePlan {
  id: string;
  name: string;
  description: string | null;
  amount: number | null;
  currency: string;
  interval: string | null;
  interval_count: number | null;
  nickname: string | null;
  metadata: Record<string, string>;
  marketing_features: Array<{ name: string }> | null;
  billing_scheme: string;
  tiers_mode: string | null;
  tiers: Array<{ up_to: number | null; unit_amount: number | null; flat_amount: number | null }> | null;
}

interface LandingPlans {
  familyPrice: string | null;
  familyYearlyNote: string | null;
  companyTiers: Array<{ rangeStart: number; upTo: string; price: string }>;
  companyStartingAt: string | null;
  isLoading: boolean;
}

function formatBRL(cents: number): string {
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

export function useLandingPlans(): LandingPlans {
  const { data: familyData, isLoading: famLoading } = useQuery({
    queryKey: ['landing-plans', 'family'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-subscription-plans', {
        body: { type: 'family' },
      });
      if (error) throw error;
      return (data?.plans as StripePlan[]) || [];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const { data: companyData, isLoading: compLoading } = useQuery({
    queryKey: ['landing-plans', 'company'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-subscription-plans', {
        body: { type: 'company' },
      });
      if (error) throw error;
      return (data?.plans as StripePlan[]) || [];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Family: pick the monthly price (first one sorted by amount)
  const familyMonthly = familyData?.find(p => p.interval === 'month');
  const familyYearly = familyData?.find(p => p.interval === 'year');

  const familyMonthlyAmount = familyMonthly?.amount ?? familyMonthly?.tiers?.[0]?.unit_amount ?? null;
  const familyYearlyAmount = familyYearly?.amount ?? familyYearly?.tiers?.[0]?.unit_amount ?? null;

  const familyPrice = familyMonthlyAmount != null ? formatBRL(familyMonthlyAmount) : null;
  const familyYearlyNote = familyYearlyAmount != null
    ? `ou ${formatBRL(familyYearlyAmount)}/ano (economize 2 meses)`
    : null;

  // Company: extract tiers or use flat price
  const companyPlan = companyData?.[0];
  let companyTiers: Array<{ rangeStart: number; upTo: string; price: string }> = [];
  let companyStartingAt: string | null = null;

  if (companyPlan?.tiers && companyPlan.tiers.length > 0) {
    let lowestPrice = Infinity;
    let currentStart = 1;
    companyTiers = companyPlan.tiers.map((tier) => {
      const price = tier.unit_amount ?? 0;
      if (price < lowestPrice) lowestPrice = price;
      const start = currentStart;
      currentStart = (tier.up_to ?? 0) + 1;
      return {
        rangeStart: start,
        upTo: tier.up_to ? `${tier.up_to}` : 'last',
        price: formatBRL(price),
      };
    });
    companyStartingAt = lowestPrice < Infinity ? formatBRL(lowestPrice) : null;
  } else if (companyPlan?.amount != null) {
    companyStartingAt = formatBRL(companyPlan.amount);
  }

  return {
    familyPrice,
    familyYearlyNote,
    companyTiers,
    companyStartingAt,
    isLoading: famLoading || compLoading,
  };
}
